/**
 * Change log reads (who changed what, when) for the company, person and role pages.
 *
 *   sf-api           GET /admin/v1/companies/:id/history        admin:companies:read
 *   sf-api           GET /admin/v1/users/history?email=          admin:users:read
 *   sf-auth-service  GET /users/:id/history                      admin:users:read
 *   sf-auth-service  GET /roles/:id/history                      role read
 *   sf-auth-service  GET /identity/sso-config/history?companyId=  admin:sso:read
 *
 * Every read answers the same page shape (`changeLogTypes.ts`), newest first. A person's history
 * lives in both backends: roles and account changes in the sign-in service, their company list in
 * sf-api. A company's lives in sf-api, plus its single sign-on setup changes in the sign-in
 * service for viewers who can read single sign-on. Each source keeps its own page cursor; "Load more" asks every source that has more for
 * its next page, and the drawer merges what has been loaded by time. One source failing still
 * shows the other, with a warning naming the part that failed.
 */
import type { ChangeLogSubject, HistoryItem, HistoryPage } from './changeLogTypes';
import { parseHistoryPage } from './parseHistoryPage';

export const CHANGE_LOG_PAGE_SIZE = 20;

/** A data provider's `custom` GET; it unwraps sf-api's `{ success, data }` envelope. */
export type CustomGet = (args: {
  url: string;
  method: 'get';
  query?: Record<string, unknown>;
}) => Promise<{ data: unknown }>;

/** The two backends' requests: `platform` is sf-api, `auth` is sf-auth-service. */
export interface HistoryRequests {
  platform: CustomGet;
  auth: CustomGet;
}

async function getHistoryPage(
  request: CustomGet,
  url: string,
  page: number,
  limit: number,
  extra: Record<string, unknown> = {},
): Promise<HistoryPage> {
  const { data } = await request({ url, method: 'get', query: { ...extra, page, limit } });
  const parsed = parseHistoryPage(data);
  if (!parsed) throw new Error('The change log answer was not in the expected shape.');
  return parsed;
}

export const fetchCompanyHistory = (
  request: CustomGet,
  companyId: string,
  page = 1,
  limit = CHANGE_LOG_PAGE_SIZE,
) =>
  getHistoryPage(
    request,
    `/admin/v1/companies/${encodeURIComponent(companyId)}/history`,
    page,
    limit,
  );

export const fetchCompanySsoHistory = (
  request: CustomGet,
  companyId: string,
  page = 1,
  limit = CHANGE_LOG_PAGE_SIZE,
) => getHistoryPage(request, '/identity/sso-config/history', page, limit, { companyId });

export const fetchRoleHistory = (
  request: CustomGet,
  roleId: string,
  page = 1,
  limit = CHANGE_LOG_PAGE_SIZE,
) => getHistoryPage(request, `/roles/${encodeURIComponent(roleId)}/history`, page, limit);

export const fetchAuthUserHistory = (
  request: CustomGet,
  authUserId: string,
  page = 1,
  limit = CHANGE_LOG_PAGE_SIZE,
) => getHistoryPage(request, `/users/${encodeURIComponent(authUserId)}/history`, page, limit);

export const fetchPlatformUserHistory = (
  request: CustomGet,
  email: string,
  page = 1,
  limit = CHANGE_LOG_PAGE_SIZE,
) =>
  getHistoryPage(request, '/admin/v1/users/history', page, limit, {
    email: email.trim().toLowerCase(),
  });

/** One place a subject's history is read from. */
export interface HistorySource {
  key: string;
  /** Shown when this part fails while another part loaded. */
  failureMessage: string;
  fetchPage: (page: number, limit: number) => Promise<HistoryPage>;
}

export function historySources(
  subject: ChangeLogSubject,
  requests: HistoryRequests,
): HistorySource[] {
  if (subject.kind === 'company') {
    const { companyId } = subject;
    const sources: HistorySource[] = [
      {
        key: 'platform',
        failureMessage: 'Company history could not be loaded',
        fetchPage: (page, limit) => fetchCompanyHistory(requests.platform, companyId, page, limit),
      },
    ];
    if (subject.includeSso) {
      sources.push({
        key: 'sso',
        failureMessage: 'Single sign-on history could not be loaded',
        fetchPage: (page, limit) => fetchCompanySsoHistory(requests.auth, companyId, page, limit),
      });
    }
    return sources;
  }
  if (subject.kind === 'role') {
    return [
      {
        key: 'auth',
        failureMessage: 'Role history could not be loaded',
        fetchPage: (page, limit) => fetchRoleHistory(requests.auth, subject.roleId, page, limit),
      },
    ];
  }
  const { authUserId, email } = subject;
  const sources: HistorySource[] = [
    {
      key: 'auth',
      failureMessage: 'Role and account history could not be loaded',
      fetchPage: (page, limit) => fetchAuthUserHistory(requests.auth, authUserId, page, limit),
    },
  ];
  if (email?.trim()) {
    sources.push({
      key: 'platform',
      failureMessage: 'Company list history could not be loaded',
      fetchPage: (page, limit) => fetchPlatformUserHistory(requests.platform, email, page, limit),
    });
  }
  return sources;
}

/** The next page to read from each source; null once a source has no more (or failed). */
export type HistoryCursor = Record<string, number | null>;

/** What one "load" brought back from every source that still had more. */
export interface HistoryStep {
  items: HistoryItem[];
  next: HistoryCursor;
  /** Failure messages of the sources that failed in this step. */
  failures: string[];
}

export const firstCursor = (sources: HistorySource[]): HistoryCursor =>
  Object.fromEntries(sources.map((s) => [s.key, 1]));

export const hasMoreHistory = (cursor: HistoryCursor | undefined) =>
  !!cursor && Object.values(cursor).some((page) => page !== null);

/**
 * Reads the next page of every source that has more. A failed source stops (its warning shows);
 * the read only fails when every source asked failed, so there is nothing new to show.
 */
export async function loadHistoryStep(
  sources: HistorySource[],
  cursor: HistoryCursor,
  limit: number = CHANGE_LOG_PAGE_SIZE,
  onSourceError?: (error: unknown) => void,
): Promise<HistoryStep> {
  const asked = sources.filter((s) => typeof cursor[s.key] === 'number');
  const results = await Promise.allSettled(
    asked.map((s) => s.fetchPage(cursor[s.key] as number, limit)),
  );
  const step: HistoryStep = { items: [], next: { ...cursor }, failures: [] };
  let firstError: unknown;
  results.forEach((result, i) => {
    const source = asked[i];
    if (result.status === 'rejected') {
      firstError ??= result.reason;
      onSourceError?.(result.reason);
      step.failures.push(source.failureMessage);
      step.next[source.key] = null;
      return;
    }
    const { items, total, page, limit: pageLimit } = result.value;
    step.items.push(...items);
    step.next[source.key] = items.length > 0 && page * pageLimit < total ? page + 1 : null;
  });
  if (asked.length > 0 && step.failures.length === asked.length) throw firstError;
  return step;
}

/** Every loaded entry once, newest first. */
export function mergeHistory(steps: HistoryStep[]): HistoryItem[] {
  const byKey = new Map<string, HistoryItem>();
  for (const step of steps) {
    for (const item of step.items) byKey.set(`${item.source}:${item.id}`, item);
  }
  return [...byKey.values()].sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
}
